import type { HeldGift, Reaction } from "@app/protocol";
import type { ShownReaction } from "../store";

/** How long a reaction stays by its pod (decision 141). */
export const BUBBLE_MS = 3_000;

/** The emoji reactions draw their glyph; the six quick messages are words from the dictionary. */
export const EMOJI: Partial<Record<Reaction, string>> = {
  smile: "😀",
  laugh: "😂",
  surprised: "😮",
  sad: "😢",
  angry: "😡",
  thumbs: "👍",
  clap: "👏",
  fire: "🔥",
};

/** The reaction to show by a player's pod now: their latest within three seconds, unless you hid them. Pure. */
export function bubbleFor(
  reactions: readonly ShownReaction[],
  userId: string,
  muted: readonly string[],
  now: number,
): ShownReaction | null {
  if (muted.includes(userId)) return null;
  const latest = [...reactions].reverse().find((r) => r.from === userId);
  return latest !== undefined && now - latest.at < BUBBLE_MS ? latest : null;
}

/** The gift to draw on a player's picture: none when gifts are hidden or its sender is muted. Pure. */
export function giftFor(
  gifts: Readonly<Record<string, HeldGift>>,
  userId: string,
  muted: readonly string[],
  hidden: boolean,
): HeldGift | null {
  const held = gifts[userId];
  if (hidden || held === undefined || muted.includes(held.from)) return null;
  return held;
}

/** Gifts that arrived since the last snapshot, to slide in from their sender. Pure. */
export function arrivals(
  before: Readonly<Record<string, HeldGift>> | null,
  after: Readonly<Record<string, HeldGift>>,
): (HeldGift & { to: string })[] {
  if (before === null) return [];
  return Object.entries(after)
    .filter(([to, held]) => before[to]?.at !== held.at)
    .map(([to, held]) => ({ ...held, to }));
}
